/**
 * DOI Custom Wishlist
 * Replaces Swym Wishlist Plus with a native localStorage-based wishlist.
 * v2: Klaviyo event tracking, share wishlist, low stock badges
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'doi_wishlist';
  var PRODUCT_CACHE = {};
  var MAX_CONCURRENT = 3;
  var fetchQueue = [];
  var activeFetches = 0;
  var LOW_STOCK_THRESHOLD = 3;
  var DASHBOARD_URL = 'https://doi-dashboard-production-ff3c.up.railway.app';

  // ─── Dashboard event tracking ─────────────────────────────────────
  function trackDashboard(action, handle) {
    try {
      var payload = { action: action, handle: handle, ts: Date.now() };
      if (window.Customer && window.Customer.email) {
        payload.email = window.Customer.email;
        payload.customer_id = String(window.Customer.id);
      } else if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
        payload.customer_id = window.ShopifyAnalytics.meta.page.customerId || null;
      }
      navigator.sendBeacon(DASHBOARD_URL + '/api/wishlist/event',
        new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    } catch (e) { /* fail silently */ }
  }

  // ─── State ──────────────────────────────────────────────────────────
  function getHandles() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveHandles(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) { /* quota exceeded — degrade silently */ }
  }

  function has(handle) {
    return getHandles().indexOf(handle) !== -1;
  }

  function add(handle) {
    var handles = getHandles();
    if (handles.indexOf(handle) === -1) {
      handles.unshift(handle);
      saveHandles(handles);
      dispatch('wishlist:add', handle);
      trackKlaviyoWishlist('add', handle);
      trackDashboard('add', handle);
    }
    refresh();
  }

  function remove(handle) {
    var handles = getHandles().filter(function (h) { return h !== handle; });
    saveHandles(handles);
    dispatch('wishlist:remove', handle);
    trackKlaviyoWishlist('remove', handle);
    trackDashboard('remove', handle);
    refresh();
  }

  function toggle(handle) {
    if (has(handle)) {
      remove(handle);
    } else {
      add(handle);
    }
  }

  function getCount() {
    return getHandles().length;
  }

  function dispatch(name, handle) {
    window.dispatchEvent(new CustomEvent(name, { detail: { handle: handle, count: getCount() } }));
    window.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { count: getCount() } }));
  }

  // ─── Klaviyo Integration ──────────────────────────────────────────
  function trackKlaviyoWishlist(action, handle) {
    // Fetch product data then push event to Klaviyo
    fetchProduct(handle, function (product) {
      if (!product) return;

      var _learnq = window._learnq || [];
      var variant = product.variants[0];
      var imageUrl = product.featured_image || (product.images && product.images[0]) || '';
      if (typeof imageUrl !== 'string') imageUrl = imageUrl.src || '';

      var payload = {
        ProductName: product.title,
        ProductID: product.id,
        Handle: product.handle,
        SKU: variant.sku || '',
        Categories: product.product_type ? [product.product_type] : [],
        ImageURL: imageUrl,
        URL: window.location.origin + '/products/' + product.handle,
        Brand: product.vendor || 'Daughters of India',
        Price: parseFloat(variant.price).toFixed(2),
        CompareAtPrice: variant.compare_at_price ? parseFloat(variant.compare_at_price).toFixed(2) : null,
        VariantID: variant.id
      };

      if (action === 'add') {
        _learnq.push(['track', 'Wishlisted Product', payload]);
      } else {
        _learnq.push(['track', 'Removed from Wishlist', payload]);
      }

      // Sync full wishlist as profile property (for segmentation)
      syncKlaviyoWishlistProperty();
    });
  }

  function syncKlaviyoWishlistProperty() {
    var _learnq = window._learnq || [];
    var handles = getHandles();
    _learnq.push(['identify', {
      WishlistItems: handles.join(','),
      WishlistCount: handles.length
    }]);
  }

  // ─── Klaviyo Price Re-sync (one-time migration) ─────────────────
  // Fixes historical wishlist events that had prices divided by 100.
  // Re-tracks each wishlisted product with the correct price on first
  // visit after the fix, then sets a flag so it never runs again.
  function resyncKlaviyoPrices() {
    var FLAG = 'doi_wishlist_price_v2';
    if (localStorage.getItem(FLAG)) return;

    var handles = getHandles();
    if (handles.length === 0) {
      localStorage.setItem(FLAG, '1');
      return;
    }

    fetchProducts(handles, function (products) {
      var _learnq = window._learnq || [];
      products.forEach(function (product) {
        if (!product) return;
        var variant = product.variants[0];
        var imageUrl = product.featured_image || (product.images && product.images[0]) || '';
        if (typeof imageUrl !== 'string') imageUrl = imageUrl.src || '';

        _learnq.push(['track', 'Wishlisted Product', {
          ProductName: product.title,
          ProductID: product.id,
          Handle: product.handle,
          SKU: variant.sku || '',
          Categories: product.product_type ? [product.product_type] : [],
          ImageURL: imageUrl,
          URL: window.location.origin + '/products/' + product.handle,
          Brand: product.vendor || 'Daughters of India',
          Price: parseFloat(variant.price).toFixed(2),
          CompareAtPrice: variant.compare_at_price ? parseFloat(variant.compare_at_price).toFixed(2) : null,
          VariantID: variant.id
        }]);
      });
      localStorage.setItem(FLAG, '1');
    });
  }

  // ─── Share Wishlist ───────────────────────────────────────────────
  function getShareUrl() {
    var handles = getHandles();
    if (handles.length === 0) return null;
    return window.location.origin + '/pages/wishlist?shared=' + encodeURIComponent(handles.join(','));
  }

  function getSharedHandles() {
    var params = new URLSearchParams(window.location.search);
    var shared = params.get('shared');
    if (!shared) return null;
    return shared.split(',').filter(Boolean);
  }

  function copyShareLink() {
    var url = getShareUrl();
    if (!url) return false;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    } else {
      // Fallback for older browsers
      var input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    return true;
  }

  // ─── DOM Binding ────────────────────────────────────────────────────
  function bindButtons(root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll('[data-wishlist-button]');

    buttons.forEach(function (btn) {
      if (btn._wishlistBound) return;
      btn._wishlistBound = true;

      var handle = btn.getAttribute('data-product-handle');
      if (!handle) {
        // Try to find handle from parent thumbnail
        var thumb = btn.closest('[data-product-handle]');
        if (thumb) handle = thumb.getAttribute('data-product-handle');
      }
      if (!handle) return;

      // Set initial state
      syncButtonState(btn, handle);

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggle(handle);
        animateHeart(btn);
      });
    });
  }

  function syncButtonState(btn, handle) {
    var isActive = has(handle);
    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function animateHeart(btn) {
    btn.classList.add('is-animating');
    setTimeout(function () {
      btn.classList.remove('is-animating');
    }, 400);
  }

  function refresh() {
    // Update all buttons on page
    document.querySelectorAll('[data-wishlist-button]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) {
        var thumb = btn.closest('[data-product-handle]');
        if (thumb) handle = thumb.getAttribute('data-product-handle');
      }
      if (handle) syncButtonState(btn, handle);
    });

    // Update header badge
    updateBadge();

    // Update wishlist page if on it
    if (document.querySelector('[data-wishlist]')) {
      renderWishlistPage();
    }
  }

  // ─── Header Badge ──────────────────────────────────────────────────
  function updateBadge() {
    var badges = document.querySelectorAll('[data-wishlist-count]');
    var count = getCount();
    badges.forEach(function (badge) {
      badge.textContent = count > 0 ? count : '';
    });
  }

  // ─── Wishlist Page Rendering ───────────────────────────────────────
  function renderWishlistPage() {
    var container = document.querySelector('[data-wishlist-container]');
    var section = document.querySelector('[data-wishlist]');
    if (!container || !section) return;

    // Check for shared wishlist view
    var sharedHandles = getSharedHandles();
    var isSharedView = !!sharedHandles;
    var handles = isSharedView ? sharedHandles : getHandles();

    if (handles.length === 0) {
      section.classList.add('is-empty');
      container.querySelector('.c-wishlist__list')?.remove();
      container.querySelector('.c-wishlist__share-bar')?.remove();
      return;
    }

    section.classList.remove('is-empty');

    // Render share bar (only for own wishlist, not shared view)
    renderShareBar(container, isSharedView, handles.length);

    // Show shared banner if viewing someone else's
    renderSharedBanner(container, isSharedView);

    // Show loading state
    var existingList = container.querySelector('.c-wishlist__list');
    if (!existingList) {
      existingList = document.createElement('ul');
      existingList.className = 'c-wishlist__list';
      container.appendChild(existingList);
    }

    // Fetch all product data then render
    fetchProducts(handles, function (products) {
      var html = '';
      products.forEach(function (product) {
        if (product) html += renderProductCard(product, isSharedView);
      });
      existingList.innerHTML = html;

      // Bind remove buttons and ATC buttons
      existingList.querySelectorAll('[data-wishlist-remove]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var h = btn.getAttribute('data-product-handle');
          remove(h);
        });
      });

      existingList.querySelectorAll('[data-wishlist-atc]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          addToCart(btn);
        });
      });

      // Bind "Add All to My Wishlist" on shared view
      var addAllBtn = container.querySelector('[data-wishlist-add-all]');
      if (addAllBtn) {
        addAllBtn.addEventListener('click', function (e) {
          e.preventDefault();
          sharedHandles.forEach(function (h) {
            if (!has(h)) add(h);
          });
          addAllBtn.textContent = 'Added!';
          addAllBtn.disabled = true;
          setTimeout(function () {
            window.location.href = '/pages/wishlist';
          }, 800);
        });
      }
    });
  }

  function renderShareBar(container, isSharedView, count) {
    // Share bar removed — not needed
    var existing = container.querySelector('.c-wishlist__share-bar');
    if (existing) existing.remove();
  }

  function renderSharedBanner(container, isSharedView) {
    var existing = container.querySelector('.c-wishlist__shared-banner');
    if (!isSharedView) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;

    var banner = document.createElement('div');
    banner.className = 'c-wishlist__shared-banner';
    banner.innerHTML =
      '<p>You\'re viewing a shared wishlist</p>' +
      '<button type="button" class="o-btn is-secondary c-wishlist__add-all-btn" data-wishlist-add-all>Add All to My Wishlist</button>';
    container.insertBefore(banner, container.firstChild);
  }

  function renderProductCard(product, isSharedView) {
    var variant = product.variants[0];
    var available = typeof product.available === 'boolean'
      ? product.available
      : product.variants.some(function (v) { return v.available || (v.inventory_quantity != null && v.inventory_quantity > 0); });
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var imageUrl = typeof image === 'string' ? image : image.src || '';

    // Price formatting using the store's currency format
    var price = formatMoney(variant.price);
    var comparePrice = variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';

    // Extract colour from title
    var colour = '';
    if (product.title.indexOf(' ~ ') > -1) {
      colour = product.title.split(' ~ ')[1] || '';
    } else if (product.title.indexOf(' - ') > -1) {
      colour = product.title.split(' - ')[1] || '';
    }

    // Check inventory for low stock badge
    var totalInventory = 0;
    product.variants.forEach(function (v) {
      if (v.inventory_quantity != null) totalInventory += v.inventory_quantity;
    });
    var isLowStock = available && totalInventory > 0 && totalInventory <= LOW_STOCK_THRESHOLD;
    var isSoldOut = !available;

    // On shared view, show add-to-wishlist button instead of remove
    var wishlistBtnHtml = isSharedView
      ? '<button type="button" class="o-product-thumbnail__wishlist-btn o-wishlist__wishlist-button' + (has(product.handle) ? ' is-active' : '') + '" ' +
          'data-wishlist-button data-product-handle="' + product.handle + '" aria-label="Add to wishlist">' +
          '<svg class="o-wishlist__wishlist-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M16.7858 10.0294L9.96018 17L3.13461 10.0294C2.40014 9.25796 1.99259 8.2223 2.0001 7.14642C2.00761 6.07054 2.42958 5.04091 3.17474 4.28021C3.91991 3.51951 4.92838 3.08887 5.98206 3.08143C7.03575 3.07398 8.04996 3.49033 8.80536 4.24044L9.1202 4.56191L9.96018 5.41959L10.8002 4.56191L11.115 4.24044C11.4853 3.85144 11.9277 3.54147 12.4164 3.32854C12.9051 3.11561 13.4303 3.00397 13.9616 3.0001C14.4929 2.99624 15.0197 3.10022 15.5113 3.30602C16.0029 3.51181 16.4495 3.81531 16.8253 4.19888C17.201 4.58245 17.4983 5.03844 17.7 5.54035C17.9016 6.04227 18.0036 6.5801 17.9999 7.12259C17.9962 7.66509 17.887 8.20142 17.6786 8.70044C17.4701 9.19946 17.1667 9.65121 16.7858 10.0294Z" stroke="currentColor"/>' +
          '</svg>' +
        '</button>'
      : '<button type="button" class="o-product-thumbnail__wishlist-btn o-wishlist__wishlist-button is-active" ' +
          'data-wishlist-remove data-product-handle="' + product.handle + '" aria-label="Remove from wishlist">' +
          '<svg class="o-wishlist__wishlist-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M16.7858 10.0294L9.96018 17L3.13461 10.0294C2.40014 9.25796 1.99259 8.2223 2.0001 7.14642C2.00761 6.07054 2.42958 5.04091 3.17474 4.28021C3.91991 3.51951 4.92838 3.08887 5.98206 3.08143C7.03575 3.07398 8.04996 3.49033 8.80536 4.24044L9.1202 4.56191L9.96018 5.41959L10.8002 4.56191L11.115 4.24044C11.4853 3.85144 11.9277 3.54147 12.4164 3.32854C12.9051 3.11561 13.4303 3.00397 13.9616 3.0001C14.4929 2.99624 15.0197 3.10022 15.5113 3.30602C16.0029 3.51181 16.4495 3.81531 16.8253 4.19888C17.201 4.58245 17.4983 5.03844 17.7 5.54035C17.9016 6.04227 18.0036 6.5801 17.9999 7.12259C17.9962 7.66509 17.887 8.20142 17.6786 8.70044C17.4701 9.19946 17.1667 9.65121 16.7858 10.0294Z" stroke="currentColor"/>' +
          '</svg>' +
        '</button>';

    // Badges (low stock / sold out) — matches collection page badge markup exactly
    var badgeHtml = '';
    if (isSoldOut) {
      badgeHtml = '<div class="o-btn o-product-thumbnail__badge is-badge" aria-label="Out of Stock"><span>Out of Stock</span></div>';
    } else if (isLowStock) {
      badgeHtml = '<div class="o-btn o-product-thumbnail__badge is-badge" aria-label="Only ' + totalInventory + ' left"><span>Only ' + totalInventory + ' left</span></div>';
    }

    return '<li class="c-wishlist__item o-product-thumbnail">' +
      '<div class="o-product-thumbnail__inner">' +
        '<div class="o-product-thumbnail__top-wrapper">' +
          wishlistBtnHtml +
          '<a href="/products/' + product.handle + '" class="o-product-thumbnail__media-wrapper" aria-label="' + escapeHtml(product.title) + '">' +
            badgeHtml +
            (imageUrl ? '<img class="o-product-thumbnail__image o-media is-primary" src="' + getSizedImage(imageUrl, '476x') + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="476" height="633">' : '') +
          '</a>' +
        '</div>' +
        '<div class="o-product-thumbnail__content">' +
          '<a href="/products/' + product.handle + '" class="o-product-thumbnail__title o-heading--6">' + escapeHtml(product.title) + '</a>' +
          '<div class="o-product-thumbnail__price-and-colour">' +
            (colour ? '<span class="o-product-thumbnail__colour o-paragraph--2">' + escapeHtml(colour) + '</span><span class="o-product-thumbnail__dot-divider"></span>' : '') +
            '<div class="o-pricing o-product-thumbnail__pricing' + (comparePrice ? ' has-compare' : '') + '">' +
              (comparePrice ? '<span class="o-pricing__compare">' + comparePrice + '</span>' : '') +
              '<span class="o-pricing__price">' + price + '</span>' +
            '</div>' +
          '</div>' +
          '<div class="c-wishlist__actions">' +
            (available
              ? '<button type="button" class="o-btn is-primary c-wishlist__atc-btn" data-wishlist-atc data-variant-id="' + variant.id + '">Add to Cart</button>'
              : '<span class="o-btn is-primary is-disabled c-wishlist__atc-btn">Sold Out</span>') +
          '</div>' +
        '</div>' +
      '</div>' +
    '</li>';
  }

  // ─── Product Fetching ──────────────────────────────────────────────
  function fetchProducts(handles, callback) {
    var results = new Array(handles.length);
    var completed = 0;

    if (handles.length === 0) {
      callback([]);
      return;
    }

    handles.forEach(function (handle, index) {
      fetchProduct(handle, function (product) {
        results[index] = product;
        completed++;
        if (completed === handles.length) {
          callback(results.filter(Boolean));
        }
      });
    });
  }

  function fetchProduct(handle, callback) {
    // Check cache first
    if (PRODUCT_CACHE[handle]) {
      callback(PRODUCT_CACHE[handle]);
      return;
    }

    // Queue the fetch
    fetchQueue.push({ handle: handle, callback: callback });
    processQueue();
  }

  function processQueue() {
    while (fetchQueue.length > 0 && activeFetches < MAX_CONCURRENT) {
      var item = fetchQueue.shift();
      activeFetches++;

      (function (handle, cb) {
        fetch('/products/' + handle + '.json')
          .then(function (res) {
            if (!res.ok) throw new Error('Not found');
            return res.json();
          })
          .then(function (data) {
            PRODUCT_CACHE[handle] = data.product;
            cb(data.product);
          })
          .catch(function () {
            // Product no longer exists — remove from wishlist silently
            var handles = getHandles().filter(function (h) { return h !== handle; });
            saveHandles(handles);
            cb(null);
          })
          .finally(function () {
            activeFetches--;
            processQueue();
          });
      })(item.handle, item.callback);
    }
  }

  // ─── Add to Cart ───────────────────────────────────────────────────
  function addToCart(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.textContent = 'Adding...';
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(function () {
        btn.textContent = 'Added!';
        btn.classList.add('is-added');
        // Update cart count in header
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        var counters = document.querySelectorAll('[data-cart-counter]');
        counters.forEach(function (c) { c.textContent = cart.item_count; });
        // Update Cart.data for cart drawer
        if (window.Cart) window.Cart.data = cart;
        // Dispatch event for cart drawer to react
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        setTimeout(function () {
          btn.textContent = 'Add to Cart';
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 1500);
      })
      .catch(function () {
        btn.textContent = 'Error — try again';
        btn.disabled = false;
        setTimeout(function () { btn.textContent = 'Add to Cart'; }, 2000);
      });
  }

  // ─── Helpers ───────────────────────────────────────────────────────
  function formatMoney(price) {
    if (typeof price === 'string') price = parseFloat(price);
    var format = (window.Currency && window.Currency.format) || '${{amount}}';
    var amount = price.toFixed(2);
    var amountNoDecimals = Math.floor(price).toString();
    var amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function getSizedImage(url, size) {
    if (!url) return '';
    // Shopify image URL sizing: insert _SIZEx before extension
    return url.replace(/\.(jpg|jpeg|png|gif|webp)/, '_' + size + '.$1');
  }

  // ─── MutationObserver (dynamic content: quick view, infinite scroll) ─
  function observeDynamicContent() {
    var observer = new MutationObserver(function (mutations) {
      var shouldBind = false;
      mutations.forEach(function (m) {
        if (m.addedNodes.length) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType === 1 && (node.querySelector('[data-wishlist-button]') || node.matches('[data-wishlist-button]'))) {
              shouldBind = true;
            }
          });
        }
      });
      if (shouldBind) bindButtons();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ─── Cross-tab Sync ────────────────────────────────────────────────
  function listenForStorageChanges() {
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) {
        refresh();
      }
    });
  }

  // ─── Init ──────────────────────────────────────────────────────────
  function init() {
    bindButtons();
    updateBadge();
    observeDynamicContent();
    listenForStorageChanges();
    resyncKlaviyoPrices();

    // If on the wishlist page, render it
    if (document.querySelector('[data-wishlist]')) {
      renderWishlistPage();
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use (e.g. quick view integration)
  window.DOIWishlist = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    getHandles: getHandles,
    getCount: getCount,
    bindButtons: bindButtons,
    refresh: refresh,
    getShareUrl: getShareUrl,
    copyShareLink: copyShareLink
  };
})();
